import { useEffect, useMemo, useState, useTransition } from "react";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { CabecalhoPagina, Tela } from "@/components/casca/CabecalhoPagina";
import { Avatar } from "@/components/conversas/Avatar";
import { Icone } from "@/components/Icone";
import { formatarTelefone } from "@/lib/painel/telefone";
import type { ModeloMensagem } from "@/lib/modelos";
import { campanhaAction, removerFichaAction, salvarFichaAction, type PreviaCampanha } from "@/web/src/acoes";
import type { Paciente } from "@/lib/telas/pacientes";

const CAMPO =
  "w-full rounded-[10px] border border-[var(--color-borda)] bg-[var(--color-painel)] px-3 py-2 text-sm text-[var(--color-tinta)] outline-none transition-colors placeholder:text-[var(--color-fraco)] focus:border-[var(--color-marca)] focus:bg-[var(--color-cartao)]";
const BOTAO_PRIMARIO =
  "flex h-9 items-center gap-1.5 rounded-full bg-[var(--color-marca)] px-4 text-[13px] font-semibold text-[var(--color-sobre-marca)] transition-[filter] hover:brightness-110 disabled:opacity-40";
const BOTAO_DISCRETO =
  "flex h-9 items-center rounded-full px-4 text-[13px] font-semibold text-[var(--color-suave)] transition-colors hover:bg-[var(--color-painel)] hover:text-[var(--color-tinta)]";

const iniciaisDe = (nome: string | null) =>
  (nome ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase() || "#";

/**
 * A lista de pacientes com o que ela existe para fazer: filtrar por interesse
 * e disparar uma campanha para quem faz sentido — quem quis botox e não
 * agendou, por exemplo.
 *
 * Marcou alguém, sobe uma barra flutuante com "Enviar campanha": a ação fica
 * onde o olho está, e some quando não há ninguém marcado.
 */
export function TabelaPacientes({
  pacientes,
  interesses,
  total,
  modelos,
}: {
  pacientes: Paciente[];
  interesses: string[];
  total: number;
  modelos: ModeloMensagem[];
}) {
  const [busca, setBusca] = useState("");
  const [interesse, setInteresse] = useState("");
  const [agendou, setAgendou] = useState<"todos" | "sim" | "nao">("todos");
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [editando, setEditando] = useState<Paciente | null>(null);
  const [excluindo, setExcluindo] = useState<Paciente | null>(null);
  const [campanha, setCampanha] = useState(false);

  const q = busca.trim().toLowerCase();
  const digitos = q.replace(/\D/g, "");
  const filtrados = useMemo(
    () =>
      pacientes.filter(
        (c) =>
          (!q ||
            (c.nome ?? "").toLowerCase().includes(q) ||
            (digitos.length > 0 && c.telefone.includes(digitos)) ||
            (c.interesse ?? "").toLowerCase().includes(q)) &&
          (!interesse || c.interesse === interesse) &&
          (agendou === "todos" || (agendou === "sim" ? c.agendou : !c.agendou)),
      ),
    [pacientes, q, digitos, interesse, agendou],
  );

  const todosMarcados = filtrados.length > 0 && filtrados.every((c) => marcados.has(c.telefone));
  const alternar = (t: string) =>
    setMarcados((s) => {
      const n = new Set(s);
      if (n.has(t)) n.delete(t);
      else n.add(t);
      return n;
    });

  return (
    <Tela>
      <CabecalhoPagina
        titulo="Pacientes"
        descricao="Todo mundo que já falou com a clínica. Filtre por interesse e mande uma campanha para quem faz sentido — quem quis botox e não agendou, por exemplo."
      />

      <div className="mb-4 flex flex-wrap items-center gap-2.5">
        <label className="flex h-10 min-w-[220px] flex-1 items-center gap-2 rounded-full bg-[var(--color-cartao)] px-3.5 text-[var(--color-fraco)] shadow-cartao focus-within:shadow-[0_0_0_1.5px_var(--color-marca)]">
          <Icone nome="lupa" className="h-4 w-4 shrink-0" />
          <input
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar nome, telefone ou interesse"
            aria-label="Buscar paciente"
            className="min-w-0 flex-1 bg-transparent text-sm text-[var(--color-tinta)] outline-none placeholder:text-[var(--color-fraco)]"
          />
        </label>
        <select
          value={interesse}
          onChange={(e) => setInteresse(e.target.value)}
          aria-label="Interesse"
          className="h-10 max-w-full rounded-full border-0 bg-[var(--color-cartao)] px-3.5 text-[13.5px] font-semibold text-[var(--color-tinta)] shadow-cartao outline-none"
        >
          <option value="">Todos os interesses</option>
          {interesses.map((i) => (
            <option key={i} value={i}>
              {i}
            </option>
          ))}
        </select>
        <div className="flex gap-0.5 rounded-full bg-[var(--color-painel)] p-[3px] shadow-[inset_0_0_0_1px_var(--color-borda)]" role="group" aria-label="Agendamento">
          {(["todos", "sim", "nao"] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setAgendou(v)}
              aria-pressed={agendou === v}
              className={
                "h-[30px] rounded-full px-3.5 text-[13px] font-semibold transition-colors " +
                (agendou === v ? "bg-[var(--color-cartao)] text-[var(--color-tinta)] shadow-botao" : "text-[var(--color-suave)] hover:text-[var(--color-tinta)]")
              }
            >
              {v === "todos" ? "Todos" : v === "sim" ? "Agendou" : "Não agendou"}
            </button>
          ))}
        </div>
        <span className="num text-[13px] text-[var(--color-fraco)]">
          {filtrados.length} de {total}
        </span>
      </div>

      <div className="overflow-x-auto rounded-[14px] bg-[var(--color-cartao)] shadow-cartao">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead className="text-xs font-bold tracking-[0.05em] text-[var(--color-fraco)] uppercase">
            <tr className="border-b border-[var(--color-borda)]">
              <th className="w-11 py-3 pr-1 pl-4">
                <input
                  type="checkbox"
                  checked={todosMarcados}
                  onChange={() => setMarcados(todosMarcados ? new Set() : new Set(filtrados.map((c) => c.telefone)))}
                  className="h-[17px] w-[17px] accent-[var(--color-marca)]"
                  aria-label="Marcar todos"
                />
              </th>
              <th className="px-3.5 py-3">Nome</th>
              <th className="px-3.5 py-3">Telefone</th>
              <th className="px-3.5 py-3">Interesse</th>
              <th className="px-3.5 py-3">Etapa</th>
              <th className="px-3.5 py-3">Observações</th>
              <th className="w-[92px] px-3.5 py-3">
                <span className="sr-only">Ações</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {filtrados.map((c) => {
              const marcado = marcados.has(c.telefone);
              return (
                <tr
                  key={c.telefone}
                  className={"border-b border-[var(--color-borda)] last:border-0 " + (marcado ? "bg-[var(--color-realce)]" : "hover:bg-[var(--color-painel)]")}
                >
                  <td className="py-2.5 pr-1 pl-4">
                    <input
                      type="checkbox"
                      checked={marcado}
                      onChange={() => alternar(c.telefone)}
                      className="h-[17px] w-[17px] accent-[var(--color-marca)]"
                      aria-label={`Selecionar ${c.nome ?? formatarTelefone(c.telefone)}`}
                    />
                  </td>
                  <td className="px-3.5 py-2.5">
                    <span className="flex items-center gap-2.5 font-semibold">
                      <Avatar iniciais={iniciaisDe(c.nome)} telefone={c.telefone} tamanho="xs" />
                      {c.temConversa ? (
                        <Link to="/conversas/$telefone" params={{ telefone: c.telefone }} className="hover:text-[var(--color-marca-forte)] hover:underline">
                          {c.nome ?? "sem nome"}
                        </Link>
                      ) : (
                        (c.nome ?? <span className="font-normal text-[var(--color-fraco)]">sem nome</span>)
                      )}
                    </span>
                  </td>
                  <td className="px-3.5 py-2.5 font-mono text-[12.5px] whitespace-nowrap text-[var(--color-suave)]">{formatarTelefone(c.telefone)}</td>
                  <td className="px-3.5 py-2.5">{c.interesse ?? <span className="text-[var(--color-fraco)]">—</span>}</td>
                  <td className="px-3.5 py-2.5">
                    <span
                      className={
                        "inline-flex h-6 items-center rounded-full px-2.5 text-xs font-semibold whitespace-nowrap " +
                        (c.etapaId === "compareceu"
                          ? "bg-[var(--color-realce)] text-[var(--color-marca-forte)]"
                          : c.agendou
                            ? "bg-[var(--color-info-fundo)] text-[var(--color-info)]"
                            : "bg-[var(--color-painel)] text-[var(--color-suave)] shadow-[inset_0_0_0_1px_var(--color-borda)]")
                      }
                    >
                      {c.etapa}
                    </span>
                  </td>
                  <td className="max-w-[240px] truncate px-3.5 py-2.5 text-[var(--color-suave)]" title={c.observacoes ?? undefined}>
                    {c.observacoes ?? "—"}
                  </td>
                  <td className="px-3.5 py-2.5">
                    <div className="flex justify-end gap-0.5">
                      <BotaoIcone rotulo={`Editar a ficha de ${c.nome ?? formatarTelefone(c.telefone)}`} icone="editar" onClick={() => setEditando(c)} />
                      <BotaoIcone rotulo={`Excluir a ficha de ${c.nome ?? formatarTelefone(c.telefone)}`} icone="lixeira" perigo onClick={() => setExcluindo(c)} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtrados.length === 0 && (
          <p className="py-12 text-center text-sm text-[var(--color-suave)]">Nenhum paciente com esses filtros. Tente limpar a busca ou escolher “Todos”.</p>
        )}
      </div>

      {marcados.size > 0 && (
        <div className="pointer-events-none sticky bottom-4 mt-4 flex justify-center">
          <div
            role="status"
            className="pointer-events-auto flex items-center gap-2 rounded-full bg-[var(--color-tinta)] py-2 pr-2 pl-5 text-sm font-semibold text-[var(--color-fundo)] shadow-flutua"
          >
            <span className="num">{marcados.size === 1 ? "1 selecionado" : `${marcados.size} selecionados`}</span>
            <button type="button" onClick={() => setMarcados(new Set())} className="h-8 rounded-full px-3 text-[13px] opacity-80 hover:bg-white/15 hover:opacity-100">
              Limpar
            </button>
            <button type="button" onClick={() => setCampanha(true)} className={BOTAO_PRIMARIO + " h-8"}>
              <Icone nome="enviar" className="h-4 w-4" />
              Enviar campanha
            </button>
          </div>
        </div>
      )}

      {campanha && (
        <ModalCampanha
          modelos={modelos}
          telefones={[...marcados]}
          aoFechar={() => setCampanha(false)}
          aoTerminar={() => {
            setCampanha(false);
            setMarcados(new Set());
          }}
        />
      )}
      {editando && <ModalEditar paciente={editando} aoFechar={() => setEditando(null)} />}
      {excluindo && <ModalExcluir paciente={excluindo} aoFechar={() => setExcluindo(null)} />}
    </Tela>
  );
}

function BotaoIcone({ rotulo, icone, perigo, onClick }: { rotulo: string; icone: "editar" | "lixeira"; perigo?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={rotulo}
      title={rotulo}
      className={
        "flex h-9 w-9 items-center justify-center rounded-full text-[var(--color-fraco)] transition-colors hover:bg-[var(--color-painel)] " +
        (perigo ? "hover:text-[var(--color-alerta)]" : "hover:text-[var(--color-tinta)]")
      }
    >
      <Icone nome={icone} className="h-4 w-4" />
    </button>
  );
}

function Modal({ titulo, children, aoFechar, largo }: { titulo: string; children: React.ReactNode; aoFechar: () => void; largo?: boolean }) {
  // Esc fecha, como em qualquer janela.
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => e.key === "Escape" && aoFechar();
    document.addEventListener("keydown", tecla);
    return () => document.removeEventListener("keydown", tecla);
  }, [aoFechar]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4" onClick={aoFechar}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        className={`anim-scale max-h-[86vh] w-full overflow-y-auto ${largo ? "max-w-lg" : "max-w-md"} rounded-[18px] bg-[var(--color-elevado)] p-6 text-[var(--color-tinta)] shadow-flutua`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function ModalExcluir({ paciente, aoFechar }: { paciente: Paciente; aoFechar: () => void }) {
  const [pendente, iniciar] = useTransition();
  const quem = paciente.nome ?? formatarTelefone(paciente.telefone);
  return (
    <Modal titulo="Excluir ficha" aoFechar={aoFechar}>
      <h2 className="text-lg font-bold">Excluir a ficha de {quem}?</h2>
      <p className="mt-1.5 text-sm text-[var(--color-suave)]">Ela some daqui e do Funil. A conversa no WhatsApp continua como está.</p>
      <div className="mt-6 flex justify-end gap-2">
        <button type="button" onClick={aoFechar} className={BOTAO_DISCRETO}>
          Cancelar
        </button>
        <button
          type="button"
          disabled={pendente}
          onClick={() =>
            iniciar(async () => {
              const r = await removerFichaAction(paciente.telefone);
              if (!r.ok) return void toast.error(r.erro);
              toast.success("Ficha excluída.");
              aoFechar();
            })
          }
          className="flex h-9 items-center rounded-full bg-[var(--color-alerta)] px-4 text-[13px] font-semibold text-[var(--color-fundo)] transition-[filter] hover:brightness-110 disabled:opacity-40"
        >
          {pendente ? "Excluindo…" : "Excluir"}
        </button>
      </div>
    </Modal>
  );
}

function ModalEditar({ paciente, aoFechar }: { paciente: Paciente; aoFechar: () => void }) {
  const [nome, setNome] = useState(paciente.nome ?? "");
  const [interesse, setInteresse] = useState(paciente.interesse ?? "");
  const [observacoes, setObservacoes] = useState(paciente.observacoes ?? "");
  const [erro, setErro] = useState<string | null>(null);
  const [pendente, iniciar] = useTransition();

  return (
    <Modal titulo="Editar ficha" aoFechar={aoFechar}>
      <h2 className="text-lg font-bold">Editar ficha</h2>
      <p className="mb-5 font-mono text-[12.5px] text-[var(--color-suave)]">{formatarTelefone(paciente.telefone)}</p>

      <div className="flex flex-col gap-3.5">
        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-semibold text-[var(--color-suave)]">Nome</span>
          <input value={nome} onChange={(e) => setNome(e.target.value)} className={CAMPO} />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-semibold text-[var(--color-suave)]">Interesse</span>
          <input value={interesse} onChange={(e) => setInteresse(e.target.value)} className={CAMPO} />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-semibold text-[var(--color-suave)]">Observações</span>
          <textarea value={observacoes} onChange={(e) => setObservacoes(e.target.value)} rows={3} className={CAMPO} />
        </label>
      </div>

      {erro && <p className="mt-3 rounded-[10px] bg-[var(--color-alerta-fundo)] px-3 py-2 text-sm text-[var(--color-alerta)]">{erro}</p>}

      <div className="mt-6 flex justify-end gap-2">
        <button type="button" onClick={aoFechar} className={BOTAO_DISCRETO}>
          Cancelar
        </button>
        <button
          type="button"
          disabled={pendente}
          onClick={() =>
            iniciar(async () => {
              const r = await salvarFichaAction(paciente.telefone, { nome, interesse, observacoes });
              if (!r.ok) setErro(r.erro);
              else {
                toast.success("Ficha salva.");
                aoFechar();
              }
            })
          }
          className={BOTAO_PRIMARIO}
        >
          {pendente ? "Salvando…" : "Salvar"}
        </button>
      </div>
    </Modal>
  );
}

/**
 * Campanha em dois passos. "Ver o que vai sair" antes de "Confirmar envio":
 * modelo é pago e vai para o WhatsApp de gente de verdade.
 */
function ModalCampanha({
  modelos,
  telefones,
  aoFechar,
  aoTerminar,
}: {
  modelos: ModeloMensagem[];
  telefones: string[];
  aoFechar: () => void;
  aoTerminar: () => void;
}) {
  const [modelo, setModelo] = useState(modelos[0]?.nome ?? "");
  const [vars, setVars] = useState<string[]>([]);
  const [previa, setPrevia] = useState<PreviaCampanha | null>(null);
  const [pendente, iniciar] = useTransition();
  const m = modelos.find((x) => x.nome === modelo);
  // Campo digitado em branco deixaria "{{3}}" no texto que vai para o WhatsApp
  // de gente de verdade — visto em 07/10/2026. Nem a prévia sai sem ele.
  const faltando = m?.variaveis.find((v, i) => v.fonte === "manual" && !(vars[i] ?? "").trim());

  function disparar(confirmar: boolean) {
    iniciar(async () => {
      const r = await campanhaAction(telefones, modelo, vars, confirmar);
      if (!r.ok) return void toast.error(r.erro);
      if ("previa" in r) return setPrevia(r.previa);
      toast.success(`Enviado para ${r.enviados} ${r.enviados === 1 ? "pessoa" : "pessoas"}.${r.falhas ? ` ${r.falhas} falharam.` : ""}`);
      aoTerminar();
    });
  }

  return (
    <Modal titulo="Campanha" aoFechar={aoFechar} largo>
      <h2 className="text-lg font-bold">
        Campanha para {telefones.length} {telefones.length === 1 ? "paciente" : "pacientes"}
      </h2>
      <p className="mt-1 mb-4 text-[13px] leading-relaxed text-[var(--color-suave)]">
        Escolha o modelo e confira o texto. O nome de cada pessoa entra sozinho no lugar do nome — numa campanha ele muda a cada
        destinatário.
      </p>

      <div className="mb-4 flex flex-col gap-1.5">
        {modelos.map((x) => (
          <label
            key={x.nome}
            className={
              "flex cursor-pointer items-center gap-2.5 rounded-xl px-3 py-2.5 transition-colors " +
              (modelo === x.nome
                ? "bg-[var(--color-realce)] shadow-[inset_0_0_0_1.5px_var(--color-marca)]"
                : "shadow-[inset_0_0_0_1px_var(--color-borda)] hover:bg-[var(--color-painel)]")
            }
          >
            <input
              type="radio"
              className="accent-[var(--color-marca)]"
              checked={modelo === x.nome}
              onChange={() => {
                setModelo(x.nome);
                setVars([]);
                setPrevia(null);
              }}
            />
            <code className="flex-1 font-mono text-xs">{x.nome}</code>
            <span className="rounded-full bg-[var(--color-painel)] px-2.5 py-0.5 text-xs font-semibold text-[var(--color-suave)] shadow-[inset_0_0_0_1px_var(--color-borda)]">
              {x.categoria}
            </span>
          </label>
        ))}
        {modelos.length === 0 && <p className="text-sm text-[var(--color-suave)]">Nenhum modelo cadastrado.</p>}
      </div>

      {m?.variaveis.map((v, i) => (
        <label key={i} className="mb-3 flex flex-col gap-1.5">
          <span className="text-[13px] font-semibold text-[var(--color-suave)]">
            {v.rotulo}
            {v.fonte !== "manual" && <span className="font-normal text-[var(--color-fraco)]"> · vem da ficha de cada paciente</span>}
          </span>
          <input
            value={v.fonte !== "manual" ? "" : (vars[i] ?? "")}
            disabled={v.fonte !== "manual"}
            placeholder={v.fonte === "nome" ? "o nome de cada paciente" : v.fonte === "interesse" ? "o interesse de cada paciente" : ""}
            onChange={(e) => {
              const c = [...vars];
              c[i] = e.target.value;
              setVars(c);
              setPrevia(null);
            }}
            className={`${CAMPO} disabled:opacity-70`}
          />
        </label>
      ))}

      {previa && (
        <div className="bg-chat my-4 flex flex-col gap-2.5 rounded-[14px] p-3.5">
          <p className="text-sm font-semibold">{previa.aviso}</p>
          <p className="rotulo">Exemplos do que sai</p>
          {previa.exemplos.map((e) => (
            <div key={e.telefone} className="flex flex-col items-end gap-1">
              <p className="self-start text-xs text-[var(--color-suave)]">{e.nome ?? formatarTelefone(e.telefone)}</p>
              <p className="max-w-[92%] rounded-[14px] rounded-tr-[4px] bg-[var(--color-cartao)] px-3 py-2 text-sm whitespace-pre-wrap shadow-[inset_0_0_0_1.5px_var(--color-marca)]">
                {e.texto}
              </p>
            </div>
          ))}
        </div>
      )}

      {faltando && <p className="mt-1 text-[13px] text-[var(--color-atencao)]">Preencha “{faltando.rotulo}” para ver o que vai sair.</p>}

      <div className="mt-5 flex justify-end gap-2">
        <button type="button" onClick={aoFechar} className={BOTAO_DISCRETO}>
          Cancelar
        </button>
        {!previa ? (
          <button
            type="button"
            onClick={() => disparar(false)}
            disabled={pendente || !modelo || Boolean(faltando)}
            className="flex h-9 items-center rounded-full bg-[var(--color-cartao)] px-4 text-[13px] font-semibold text-[var(--color-tinta)] shadow-botao transition-colors hover:bg-[var(--color-painel)] disabled:opacity-40"
          >
            {pendente ? "Montando…" : "Ver o que vai sair"}
          </button>
        ) : (
          <button type="button" onClick={() => disparar(true)} disabled={pendente || Boolean(faltando)} className={BOTAO_PRIMARIO}>
            {pendente ? "Enviando…" : "Confirmar envio"}
          </button>
        )}
      </div>
    </Modal>
  );
}
